import { Helmet } from "@dr.pogodin/react-helmet";

export default function Privacy() {
  return (
    <article className="max-w-4xl mx-auto px-6 py-12">
      <Helmet>
        <title>Privacy Policy | Free1099Calc</title>
        <meta
          name="description"
          content="Privacy Policy for Free1099Calc - Learn how we collect, use, and protect your data including Google AdSense advertising practices."
        />
        <link rel="canonical" href="https://free1099calc.com/privacy" />
      </Helmet>

      <h1 className="text-3xl font-bold tracking-tight mb-2">Privacy Policy</h1>
      <p className="text-sm text-slate-600 mb-8">Last updated: November 17, 2025</p>

      <p className="text-slate-700 mb-6">
        At Free1099Calc ("we," "our," or "us"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>free1099calc.com</strong> (the "Site"). Please read this policy carefully.
      </p>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <p className="text-sm text-slate-800">
          <strong>Important:</strong> We use third-party advertising services including Google AdSense. These services may use cookies and similar technologies to serve personalized ads based on your browsing activity.
        </p>
      </div>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">1. Information We Collect</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3">Information You Provide</h3>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Calculator Inputs:</strong> All tax calculations (income, deductions, expenses) are processed locally in your browser. We do NOT send your financial data to our servers.</li>
        <li><strong>Contact Form:</strong> If you contact us via our contact form, we collect your name and email address to respond to your inquiry.</li>
        <li><strong>Local Storage:</strong> We may store your calculator preferences and settings in your browser's local storage for convenience. This data remains on your device.</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3">Automatically Collected Information</h3>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Usage Data:</strong> We collect anonymous analytics data including page views, time spent on site, device type, browser type, and geographic location (city/country level only).</li>
        <li><strong>Cookies:</strong> We and our third-party partners use cookies and similar tracking technologies. See the "Cookies" section below for details.</li>
        <li><strong>IP Address:</strong> Your IP address may be logged for security purposes and anonymized analytics.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">2. How We Use Your Information</h2>
      <p className="text-slate-700 mb-3">We use the information we collect to:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Operate and maintain the Site and calculator functionality</li>
        <li>Improve user experience and develop new features</li>
        <li>Understand how visitors use our Site through analytics</li>
        <li>Respond to your comments, questions, and customer service requests</li>
        <li>Send periodic emails if you subscribe to our newsletter (with your consent)</li>
        <li>Detect, prevent, and address technical issues or fraudulent activity</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">3. Third-Party Advertising - Google AdSense</h2>
      <p className="text-slate-700 mb-4">
        We use <strong>Google AdSense</strong> to display advertisements on our Site. Google and its partners use cookies to serve ads based on your prior visits to our Site and other websites.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3">How Google Uses Your Data</h3>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>Google uses cookies to serve ads based on your browsing history across websites</li>
        <li>Google's advertising partners may use cookies to show you personalized ads</li>
        <li>Ad personalization takes into account factors like demographics, interests, and previous ad interactions</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3">Your Advertising Choices</h3>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Opt Out of Personalized Ads:</strong> Visit <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener" className="text-blue-600 underline">Google's Ads Settings</a> to opt out of personalized advertising</li>
        <li><strong>Learn More:</strong> Read Google's <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener" className="text-blue-600 underline">Advertising Policy</a></li>
        <li><strong>NAI Opt-Out:</strong> Visit the <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener" className="text-blue-600 underline">Network Advertising Initiative opt-out page</a></li>
        <li><strong>DAA Opt-Out:</strong> Visit the <a href="https://optout.aboutads.info/" target="_blank" rel="noopener" className="text-blue-600 underline">Digital Advertising Alliance opt-out page</a></li>
      </ul>
      <p className="text-sm text-slate-600 italic mb-4">Note: Opting out of personalized ads does not mean you will see fewer ads; ads will just be less relevant to your interests.</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">4. Cookies and Tracking Technologies</h2>
      <p className="text-slate-700 mb-4">We use cookies, web beacons, and similar technologies to enhance your experience. Types of cookies we use:</p>

      <h3 className="text-xl font-semibold mt-6 mb-3">Essential Cookies</h3>
      <p className="text-slate-700 mb-4">Required for the Site to function properly (e.g., remembering your calculator settings).</p>

      <h3 className="text-xl font-semibold mt-6 mb-3">Analytics Cookies</h3>
      <p className="text-slate-700 mb-4">Help us understand how visitors interact with our Site (e.g., Google Analytics).</p>

      <h3 className="text-xl font-semibold mt-6 mb-3">Advertising Cookies</h3>
      <p className="text-slate-700 mb-4">Used by Google AdSense and advertising partners to serve relevant ads.</p>

      <h3 className="text-xl font-semibold mt-6 mb-3">Managing Cookies</h3>
      <p className="text-slate-700 mb-3">You can control cookies through your browser settings. Most browsers allow you to:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>View what cookies are stored and delete them individually or all at once</li>
        <li>Block third-party cookies</li>
        <li>Block all cookies from specific sites</li>
        <li>Block all cookies entirely</li>
      </ul>
      <p className="text-sm text-slate-600 mb-4"><strong>Warning:</strong> Blocking all cookies may limit Site functionality.</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">5. Data Sharing and Disclosure</h2>
      <p className="text-slate-700 mb-4">We do not sell, trade, or rent your personal information. We may share data with:</p>

      <h3 className="text-xl font-semibold mt-6 mb-3">Service Providers</h3>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Google AdSense:</strong> For displaying advertisements</li>
        <li><strong>Analytics Providers:</strong> For understanding Site usage (e.g., Google Analytics)</li>
        <li><strong>Hosting Providers:</strong> For Site infrastructure and content delivery</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3">Legal Requirements</h3>
      <p className="text-slate-700 mb-4">We may disclose your information if required by law or in response to valid legal requests (e.g., subpoenas, court orders).</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">6. Data Security</h2>
      <p className="text-slate-700 mb-3">We implement reasonable security measures to protect your information, including:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li>HTTPS encryption for data transmission</li>
        <li>Secure hosting infrastructure</li>
        <li>Regular security audits</li>
        <li>Access controls and authentication</li>
      </ul>
      <p className="text-slate-700 mb-4">However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">7. Your Privacy Rights</h2>
      <p className="text-slate-700 mb-3">Depending on your location, you may have the following rights:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
        <li><strong>Deletion:</strong> Request deletion of your personal data</li>
        <li><strong>Objection:</strong> Object to processing of your personal data</li>
        <li><strong>Portability:</strong> Request transfer of your data to another service</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3">California Residents (CCPA)</h3>
      <p className="text-slate-700 mb-4">California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected and the right to opt-out of the sale of personal information. We do not sell personal information.</p>

      <h3 className="text-xl font-semibold mt-6 mb-3">European Union Residents (GDPR)</h3>
      <p className="text-slate-700 mb-4">EU residents have rights under the General Data Protection Regulation (GDPR), including the right to data portability, the right to restrict processing, and the right to lodge a complaint with a supervisory authority.</p>

      <p className="text-slate-700 mb-4">To exercise these rights, contact us at: <strong>privacy@free1099calc.com</strong></p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">8. Children's Privacy</h2>
      <p className="text-slate-700 mb-4">Our Site is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">9. Changes to This Privacy Policy</h2>
      <p className="text-slate-700 mb-4">We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Material changes will be communicated via a prominent notice on our Site. Your continued use of the Site after changes constitutes acceptance of the updated policy.</p>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">10. Contact Us</h2>
      <p className="text-slate-700 mb-3">If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
      <ul className="list-disc ml-6 text-slate-700 space-y-2 mb-4">
        <li><strong>Email:</strong> privacy@free1099calc.com</li>
        <li><strong>Website:</strong> <a href="https://free1099calc.com/contact" className="text-blue-600 underline">https://free1099calc.com/contact</a></li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-4 border-b-2 border-slate-200 pb-2">11. Consent</h2>
      <p className="text-slate-700 mb-6">By using our Site, you consent to this Privacy Policy and our collection and use of information as described herein.</p>
    </article>
  );
}
